/**
 * Builds the narration script for the audio guide.
 *
 * Rules:
 * - Prefer enriched tourist content (history, fun facts, historical figures,
 *   best time to visit) that was ALREADY generated/cached — never triggers new AI.
 * - The short blurb may serve as a brief intro, but internal/technical sentences
 *   ("validada por mapa", "para manter o roteiro dentro da cidade", "tempo disponível", …)
 *   are stripped out: the audio must sound like a real tour guide.
 */

export type AudioGuideDetails = {
  history?: string | null;
  funFacts?: string[] | null;
  historicalFigures?: { name: string; note: string }[] | null;
  bestTimeToVisit?: string | null;
};

/** Sentence fragments that reveal how Routify built the itinerary. Never narrated. */
const TECHNICAL_PATTERNS: RegExp[] = [
  /valida(da|do|dos|das)\s+por\s+mapa/i,
  /verificad[oa]\s+(no|pelo)\s+mapa/i,
  /paragem\s+real/i,
  /manter\s+o\s+roteiro/i,
  /dentro\s+da\s+cidade\s+e\s+do\s+tempo/i,
  /tempo\s+dispon[ií]vel/i,
  /escolhid[oa]\s+para/i,
  /otimizad[oa]\s+para\s+o\s+(teu|seu)\s+(tempo|percurso)/i,
  /map[-\s]?validated/i,
  /real\s+stop\s+in/i,
  /chosen\s+to\s+keep/i,
  /available\s+time/i,
  /within\s+the\s+city\s+and/i,
  /elegid[oa]\s+para/i,
  /tiempo\s+disponible/i,
  /temps\s+disponible/i,
  /choisi\s+pour/i,
];

function splitSentences(text: string): string[] {
  return text
    .replace(/\s+/g, " ")
    .split(/(?<=[.!?…])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Removes internal/technical sentences from a short description. */
export function sanitizeBlurbForAudio(blurb: string | null | undefined): string {
  if (!blurb) return "";
  const kept = splitSentences(String(blurb)).filter(
    (s) => !TECHNICAL_PATTERNS.some((re) => re.test(s)),
  );
  return kept.join(" ").trim();
}

function ensureStop(text: string): string {
  const t = text.trim();
  if (!t) return "";
  return /[.!?…]$/.test(t) ? t : `${t}.`;
}

const CONNECTORS: Record<string, { facts: string; figures: string; best: string }> = {
  pt: { facts: "Curiosidades", figures: "Figuras ligadas a este local", best: "Melhor altura para visitar" },
  es: { facts: "Curiosidades", figures: "Figuras ligadas a este lugar", best: "Mejor momento para visitar" },
  fr: { facts: "Le saviez-vous", figures: "Personnages liés à ce lieu", best: "Meilleur moment pour visiter" },
  de: { facts: "Wissenswertes", figures: "Persönlichkeiten dieses Ortes", best: "Beste Besuchszeit" },
  it: { facts: "Curiosità", figures: "Figure legate a questo luogo", best: "Momento migliore per visitare" },
  ja: { facts: "豆知識", figures: "この場所にゆかりのある人物", best: "おすすめの時間" },
  en: { facts: "Did you know", figures: "People connected to this place", best: "Best time to visit" },
};

export function buildAudioGuideScript(input: {
  name: string;
  blurb?: string | null;
  details?: AudioGuideDetails | null;
  lang?: string;
}): string {
  const lang = (input.lang ?? "en").toLowerCase();
  const c = CONNECTORS[lang] ?? CONNECTORS.en;
  const parts: string[] = [];

  const name = (input.name ?? "").trim();
  if (name) parts.push(ensureStop(name));

  const intro = sanitizeBlurbForAudio(input.blurb);
  const d = input.details ?? null;
  const history = (d?.history ?? "").trim();
  const facts = (d?.funFacts ?? []).map((f) => String(f).trim()).filter(Boolean);
  const figures = (d?.historicalFigures ?? []).filter((p) => p && p.name);
  const best = (d?.bestTimeToVisit ?? "").trim();
  const hasEnriched = Boolean(history || facts.length || figures.length || best);

  if (intro) {
    // With enriched content the blurb is only a short lead-in (first sentence).
    const lead = hasEnriched ? splitSentences(intro)[0] ?? intro : intro;
    parts.push(ensureStop(lead));
  }

  if (history) parts.push(ensureStop(history));

  if (facts.length) {
    parts.push(`${c.facts}: ${facts.map((f) => ensureStop(f)).join(" ")}`);
  }

  if (figures.length) {
    const line = figures.map((p) => ensureStop(`${p.name.trim()} — ${String(p.note ?? "").trim()}`)).join(" ");
    parts.push(`${c.figures}: ${line}`);
  }

  if (best) parts.push(`${c.best}: ${ensureStop(best)}`);

  return parts.join(" ").replace(/\s+/g, " ").trim();
}
